import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from 'recharts';
import Header from './Header';
import ReportSignOff from './ReportSignOff';
import { useMetricReport, useLaboratoryData } from '../hooks/useLaboratoryData';
import { 
  GynAdequacySummaryData, 
  GynAdequacyBySourceData, 
  CytotechDiagnosisSummaryData, 
  PathologistDiagnosisSummaryData,
  GynPapBxCorrelationData,
  TotalPapVolumeData,
  ScreeningVsFinalDiagnosisData,
  HSILRetrospectiveReviewData,
  NonGynCytologyDiagnosisCorrelationData,
  NonGynSourceCorrelationData,
  CytotechDiagnosisAndHpvData,
  AgeAndHpvData,
  HpvTotalThinprepData,
  DiagnosisAndHpvSummaryData,
} from '../types/MedicalData';
import { exportReportData } from '../utils/exportUtils';
import { LaboratoryApiService } from '../services/api';

const ReportView: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const navigate = useNavigate();
  const { data: laboratoryData, loading: laboratoryDataLoading } = useLaboratoryData();
  const [exportLoading, setExportLoading] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  
  // Find the report data by ID
  const data = laboratoryData?.find(item => item.id === reportId);
  
  // Initialize hooks with default values - default to last month
  const getLastMonth = () => {
    const now = new Date();
    const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return {
      year: lastMonth.getFullYear(),
      monthIndex: lastMonth.getMonth()
    };
  };
  const lastMonth = getLastMonth();
  const [selectedMonth, setSelectedMonth] = useState<string>('');
  const [selectedYear, setSelectedYear] = useState<number>(lastMonth.year);
  const [selectedMonthIndex, setSelectedMonthIndex] = useState<number>(lastMonth.monthIndex);
  
  // Check if this is a special report that needs month/year selector
  const isGynAdequacySummary = reportId === 'gyn-adequacy-summary' || data?.id === 'gyn-adequacy-summary';
  const isGynAdequacyBySource = reportId === 'gyn-adequacy-by-source' || data?.id === 'gyn-adequacy-by-source';
  const isCytotechDiagnosisSummary = reportId === 'cytotech-diagnosis-summary' || data?.id === 'cytotech-diagnosis-summary';
  const isCytotechDiagnosisAndHpv = reportId === 'cytotech-diagnosis-and-hpv' || data?.id === 'cytotech-diagnosis-and-hpv';
  const isPathologistDiagnosisAndHpv = reportId === 'pathologist-diagnosis-and-hpv' || data?.id === 'pathologist-diagnosis-and-hpv';
  const isAgeAndHpv = reportId === 'age-and-hpv' || data?.id === 'age-and-hpv';
  const isHpvCasesWithThinprep = reportId === 'hpv-cases-with-thinprep' || data?.id === 'hpv-cases-with-thinprep';
  const isDiagnosisAndHpvSummary = reportId === 'diagnosis-and-hpv-summary' || data?.id === 'diagnosis-and-hpv-summary';
  const isPathologistDiagnosisSummary = reportId === 'pathologist-diagnosis-summary' || data?.id === 'pathologist-diagnosis-summary';
  const isGynDiagnosisSummary = reportId === 'gyn-diagnosis-summary' || reportId === 'gyn-diagnosis-detail' || data?.id === 'gyn-diagnosis-summary' || data?.id === 'gyn-diagnosis-detail';
  const isGynPapBxCorrelation = reportId === 'gyn-pap-bx-correlation' || data?.id === 'gyn-pap-bx-correlation';
  const isTotalPapVolume = reportId === 'pap-volume' || data?.id === 'pap-volume';
  const isScreeningVsFinalDiagnosis = reportId === 'screening-vs-final-diagnosis' || data?.id === 'screening-vs-final-diagnosis';
  const isHSILRetrospectiveReview = reportId === 'hsil-retrospective-review' || data?.id === 'hsil-retrospective-review';
  const isNonGynCytologyDiagnosisCorrelation = reportId === 'non-gyn-cytology-diagnosis-correlation' || data?.id === 'non-gyn-cytology-diagnosis-correlation';
  const isNonGynSourceCorrelation = reportId === 'non-gyn-source-correlation' || data?.id === 'non-gyn-source-correlation';
  
  // For reports that need month/year selector, format as YYYY-MM for API
  const needsMonthYearSelector = isGynAdequacySummary || isGynAdequacyBySource || isCytotechDiagnosisSummary || 
    isCytotechDiagnosisAndHpv || isPathologistDiagnosisAndHpv || isAgeAndHpv || isHpvCasesWithThinprep || isDiagnosisAndHpvSummary || isPathologistDiagnosisSummary || isGynDiagnosisSummary || isGynPapBxCorrelation || isTotalPapVolume ||
    isScreeningVsFinalDiagnosis || isHSILRetrospectiveReview || isNonGynCytologyDiagnosisCorrelation || isNonGynSourceCorrelation;
  
  const monthYearParam = needsMonthYearSelector
    ? `${selectedYear}-${String(selectedMonthIndex + 1).padStart(2, '0')}`
    : selectedMonth;
  
  const { data: reportData, loading: reportDataLoading, error } = useMetricReport(reportId || '', monthYearParam);
  
  useEffect(() => {
    if (data && data.monthlyData.length > 0) {
      setSelectedMonth(data.monthlyData[data.monthlyData.length - 1].month);
    }
  }, [data]);
  
  // Initialize month/year to last month/year for reports that need it
  useEffect(() => {
    if (needsMonthYearSelector) {
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      setSelectedYear(lastMonth.getFullYear());
      setSelectedMonthIndex(lastMonth.getMonth());
    }
  }, [needsMonthYearSelector]);
  
  // Month names for selector
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Generate year options (current year and past 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear - i);
  
  // Handle month/year change for Gyn Adequacy Summary
  const handleMonthYearChange = (monthIndex: number, year: number) => {
    setSelectedMonthIndex(monthIndex);
    setSelectedYear(year);
  };
  
  useEffect(() => {
    // Only redirect if we have finished loading laboratory data and couldn't find the specific report
    // But allow gyn-adequacy-summary and gyn-adequacy-by-source to load even if card data isn't found
    if (!laboratoryDataLoading && laboratoryData && laboratoryData.length > 0 && !data && !isGynAdequacySummary && !isGynAdequacyBySource) {
      navigate('/dashboard');
    }
  }, [data, laboratoryData, laboratoryDataLoading, isGynAdequacySummary, isGynAdequacyBySource, navigate]);
  
  // Show loading while laboratory data is loading (unless it's gyn-adequacy reports which can work without card data)
  if (laboratoryDataLoading && !isGynAdequacySummary && !isGynAdequacyBySource && !data) {
    return (
      <div className="report-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }
  
  // For special reports, allow rendering even without card data
  // For other reports, require card data
  if (!data && !needsMonthYearSelector) {
    return (
      <div className="report-view">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  const formatValue = (value: number) => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toLocaleString();
  };

  const formatDate = (dateString: string) => {
    if (!dateString || dateString === '-') return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (e) {
      return dateString; // Return original if parsing fails
    }
  };
  const gynAdequacyData = isGynAdequacySummary && reportData && 'reportDate' in reportData && 'satisfactory' in reportData
    ? reportData as GynAdequacySummaryData 
    : null;
  
  // Check if reportData has 'sources' property (GynAdequacyBySourceData structure)
  const hasSourcesStructure = reportData && 'sources' in reportData;
  
  const gynAdequacyBySourceData = isGynAdequacyBySource && hasSourcesStructure
    ? reportData as GynAdequacyBySourceData
    : null;
  
  // Check if reportData has 'overallSummary' property (CytotechDiagnosisSummaryData or PathologistDiagnosisSummaryData structure)
  const hasOverallSummaryStructure = reportData && 'overallSummary' in reportData;
  
  const cytotechDiagnosisData = isCytotechDiagnosisSummary && hasOverallSummaryStructure
    ? reportData as CytotechDiagnosisSummaryData
    : null;
  
  const pathologistDiagnosisData = isPathologistDiagnosisSummary && hasOverallSummaryStructure
    ? reportData as PathologistDiagnosisSummaryData
    : null;
  
  // Gyn Diagnosis Summary now uses GynAdequacyBySourceData format (grouped by ProcSite)
  const gynDiagnosisData = isGynDiagnosisSummary && hasSourcesStructure
    ? reportData as GynAdequacyBySourceData
    : null;
  
  // Check if reportData has 'rows' property (correlation/tabular reports)
  const hasRowsStructure = reportData && 'rows' in reportData;
  
  const gynPapBxCorrelationData = isGynPapBxCorrelation && hasRowsStructure
    ? reportData as GynPapBxCorrelationData
    : null;
  
  const screeningVsFinalDiagnosisData = isScreeningVsFinalDiagnosis && hasRowsStructure
    ? reportData as ScreeningVsFinalDiagnosisData
    : null;
  
  const hsilRetrospectiveReviewData = isHSILRetrospectiveReview && hasRowsStructure
    ? reportData as HSILRetrospectiveReviewData
    : null;
  
  const nonGynCytologyDiagnosisCorrelationData = isNonGynCytologyDiagnosisCorrelation && hasRowsStructure
    ? reportData as NonGynCytologyDiagnosisCorrelationData
    : null;
  
  const nonGynSourceCorrelationData = isNonGynSourceCorrelation && hasRowsStructure
    ? reportData as NonGynSourceCorrelationData
    : null;
  
  // Check if reportData has 'overallRows' property (CytotechDiagnosisAndHpvData structure)
  const hasOverallRowsStructure = reportData && 'overallRows' in reportData;
  
  const cytotechDiagnosisAndHpvData = isCytotechDiagnosisAndHpv && hasOverallRowsStructure
    ? reportData as CytotechDiagnosisAndHpvData
    : null;
  
  const pathologistDiagnosisAndHpvData = isPathologistDiagnosisAndHpv && hasOverallRowsStructure
    ? reportData as CytotechDiagnosisAndHpvData
    : null;
  
  // Check if reportData has 'sections' property with ageGroup (AgeAndHpvData structure)
  const hasAgeAndHpvStructure = reportData && 'sections' in reportData && 
    Array.isArray((reportData as any).sections) && 
    ((reportData as any).sections.length === 0 || 'ageGroup' in (reportData as any).sections[0]);
  
  const ageAndHpvData = isAgeAndHpv && hasAgeAndHpvStructure
    ? reportData as AgeAndHpvData
    : null;
  
  // Check if reportData has 'totalCases' and 'totalSpecimens' properties (HpvTotalThinprepData structure)
  const hasHpvTotalThinprepStructure = reportData && 'totalCases' in reportData && 'totalSpecimens' in reportData;
  
  const hpvTotalThinprepData = isHpvCasesWithThinprep && hasHpvTotalThinprepStructure
    ? reportData as HpvTotalThinprepData
    : null;
  
  // Check if reportData has 'sections' property with diagnosisCategory (DiagnosisAndHpvSummaryData structure)
  const hasDiagnosisAndHpvSummaryStructure = reportData && 'sections' in reportData && 
    Array.isArray((reportData as any).sections) && 
    ((reportData as any).sections.length === 0 || 'diagnosisCategory' in (reportData as any).sections[0]);
  
  const diagnosisAndHpvSummaryData = isDiagnosisAndHpvSummary && hasDiagnosisAndHpvSummaryStructure
    ? reportData as DiagnosisAndHpvSummaryData
    : null;
  
  // Check if reportData has 'breakdown' property (TotalPapVolumeData structure)
  const hasBreakdownStructure = reportData && 'breakdown' in reportData;
  
  const totalPapVolumeData = isTotalPapVolume && hasBreakdownStructure
    ? reportData as TotalPapVolumeData
    : null;

  // Helper function to get export data for reports (handles both rows and sections)
  const getReportExportData = (reportDataItem: any) => {
    if (!reportDataItem) return null;
    
    // Check if it has rows structure
    if ('rows' in reportDataItem) {
      if (!reportDataItem.rows || reportDataItem.rows.length === 0) return null;
      return {
        data: reportDataItem,
        count: reportDataItem.totalCases || reportDataItem.rows.length,
        title: data?.title || 'Report'
      };
    }
    
    // Check if it has sections structure (like CytotechDiagnosisSummaryData)
    if ('sections' in reportDataItem || 'overallSummary' in reportDataItem) {
      // Check if there's any data to export
      const hasOverallSummary = reportDataItem.overallSummary && reportDataItem.overallSummary.length > 0;
      const hasSections = reportDataItem.sections && reportDataItem.sections.length > 0;
      
      if (!hasOverallSummary && !hasSections) return null;
      
      return {
        data: reportDataItem,
        count: reportDataItem.totalCases || 0,
        title: data?.title || 'Report'
      };
    }
    
    // Check if it has sources structure (like GynAdequacyBySourceData)
    if ('sources' in reportDataItem) {
      if (!reportDataItem.sources || reportDataItem.sources.length === 0) return null;
      // Count total rows across all sources
      let totalRows = 0;
      reportDataItem.sources.forEach((source: any) => {
        if (source.rows) {
          totalRows += source.rows.length;
        }
      });
      if (totalRows === 0) return null;
      
      return {
        data: reportDataItem,
        count: reportDataItem.totalCases || totalRows,
        title: data?.title || 'Report'
      };
    }
    
    // Check if it has totalCases and totalSpecimens (like HpvTotalThinprepData)
    if ('totalCases' in reportDataItem && 'totalSpecimens' in reportDataItem && !('sources' in reportDataItem) && !('satisfactory' in reportDataItem)) {
      return {
        data: reportDataItem,
        count: (reportDataItem.totalCases || 0) + (reportDataItem.totalSpecimens || 0),
        title: data?.title || 'Report'
      };
    }
    
    // Check if it has satisfactory/unsatisfactory structure (like GynAdequacySummaryData)
    if ('satisfactory' in reportDataItem || 'unsatisfactory' in reportDataItem) {
      const hasSatisfactory = reportDataItem.satisfactory && reportDataItem.satisfactory.length > 0;
      const hasUnsatisfactory = reportDataItem.unsatisfactory && reportDataItem.unsatisfactory.length > 0;
      
      if (!hasSatisfactory && !hasUnsatisfactory) return null;
      
      return {
        data: reportDataItem,
        count: reportDataItem.totalCases || (reportDataItem.totalSatisfactory || 0) + (reportDataItem.totalUnsatisfactory || 0),
        title: data?.title || 'Report'
      };
    }
    
    return null;
  };

  // Handle export for report pages
  const handleReportExport = async (format: 'csv' | 'excel' | 'pdf', reportDataItem: any) => {
    const exportData = getReportExportData(reportDataItem);
    
    if (!exportData) {
      setExportError('No data available to export');
      return;
    }

    try {
      setExportLoading(true);
      setExportError(null);

      // Try to fetch sign-off information
      let signOff = null;
      if (reportId && monthYearParam) {
        try {
          signOff = await LaboratoryApiService.getReportSignOff(reportId, monthYearParam);
        } catch (err) {
          // Sign-off not found or error - that's okay, just continue without it
          console.log('No sign-off found for report:', err);
        }
      }

      const blob = exportReportData(format, exportData.data, exportData.title, signOff || undefined);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set appropriate file extension
      const extension = format === 'excel' ? 'xlsx' : format;
      const reportName = exportData.title.toLowerCase().replace(/\s+/g, '-');
      link.download = `${reportName}-${new Date().toISOString().split('T')[0]}.${extension}`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to export data';
      setExportError(errorMessage);
      console.error('Error exporting data:', err);
    } finally {
      setExportLoading(false);
    }
  };

  // Helper to get Header props for reports (handles both rows and sections)
  const getHeaderPropsForReport = (reportDataItem: any, defaultTitle: string) => {
    const reportTitle = data ? `${data.title} - Detailed Report` : defaultTitle;
    
    // Calculate count based on data structure
    let reportCount = 0;
    if (reportDataItem?.rows && reportDataItem.rows.length > 0) {
      reportCount = reportDataItem.totalCases || reportDataItem.rows.length;
    } else if (reportDataItem?.totalCases !== undefined && reportDataItem?.totalSpecimens !== undefined && !reportDataItem?.sources && !reportDataItem?.satisfactory) {
      // For HpvTotalThinprepData
      reportCount = (reportDataItem.totalCases || 0) + (reportDataItem.totalSpecimens || 0);
    } else if (reportDataItem?.sources) {
      // For GynAdequacyBySourceData
      let totalRows = 0;
      reportDataItem.sources.forEach((source: any) => {
        if (source.rows) {
          totalRows += source.rows.length;
        }
      });
      reportCount = reportDataItem.totalCases || totalRows;
    } else if (reportDataItem?.satisfactory || reportDataItem?.unsatisfactory) {
      // For GynAdequacySummaryData
      reportCount = reportDataItem.totalCases || 
        ((reportDataItem.totalSatisfactory || 0) + (reportDataItem.totalUnsatisfactory || 0)) ||
        ((reportDataItem.satisfactory?.length || 0) + (reportDataItem.unsatisfactory?.length || 0));
    } else if (reportDataItem?.sections || reportDataItem?.overallSummary) {
      // For sections-based reports, count sections and categories
      if (reportDataItem.overallSummary) {
        reportCount += reportDataItem.overallSummary.length;
      }
      if (reportDataItem.sections) {
        reportDataItem.sections.forEach((section: any) => {
          if (section.categories) {
            reportCount += section.categories.length;
          }
        });
      }
      if (reportDataItem.totalCases) {
        reportCount = reportDataItem.totalCases;
      }
    }
    
    const reportDateRange = reportDataItem?.reportDate || { startDate: '', endDate: '' };
    
    return {
      title: reportTitle,
      showBackButton: true,
      showExportButton: true,
      onBack: () => navigate('/dashboard'),
      filteredCount: reportCount,
      dateRange: reportDateRange,
      onExport: (format: 'csv' | 'excel' | 'pdf') => handleReportExport(format, reportDataItem),
      exportLoading,
      exportError
    };
  };

  if (reportDataLoading) {
    return (
      <div className="report-view">
        <Header 
          title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy Summary - Detailed Report'}
          showBackButton={true}
          onBack={() => navigate('/dashboard')}
        />
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading report data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="report-view">
        <Header 
          title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy Summary - Detailed Report'}
          showBackButton={true}
          onBack={() => navigate('/dashboard')}
        />
        <div className="error-container">
          <div className="error-message">
            <h3>Error Loading Report</h3>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Render Gyn Adequacy by Source if this is that type of report
  // Only render if we have the correct data structure with 'sources'
  if (isGynAdequacyBySource && hasSourcesStructure) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy by Source - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy by Source - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!reportData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy by Source - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // If we don't have the correct Gyn Adequacy by Source structure, show error
    if (!gynAdequacyBySourceData) {
      return (
        <div className="report-view">
          <Header 
            {...getHeaderPropsForReport(gynAdequacyBySourceData, 'Gyn Adequacy by Source - Detailed Report')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No Gyn Adequacy by Source data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy by Source - Detailed Report'}
          showBackButton={true}
          onBack={() => navigate('/dashboard')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Gyn Adequacy By Source</h1>
            <div className="report-date-range">
              {gynAdequacyBySourceData ? (
                <>
                  {formatDate(gynAdequacyBySourceData.reportDate.startDate)} - {formatDate(gynAdequacyBySourceData.reportDate.endDate)}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Source Sections */}
          {gynAdequacyBySourceData.sources.map((source, sourceIndex) => (
            <motion.section
              key={sourceIndex}
              className="adequacy-by-source-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + (sourceIndex * 0.1) }}
            >
              <h2 className="source-section-title">{source.procSite}</h2>
              <div className="adequacy-table-container">
                <table className="adequacy-by-source-table">
                  <thead>
                    <tr>
                      <th>Adequacy</th>
                      <th>Description</th>
                      <th>Total Cases</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {source.rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        <td>{row.adequacy || ''}</td>
                        <td>{row.description}</td>
                        <td className="count-cell">{row.totalCases.toLocaleString()}</td>
                        <td className="percentage-cell">{row.percentage.toFixed(2)}%</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td colSpan={2}><strong>Total</strong></td>
                      <td className="count-cell"><strong>{source.totalCases.toLocaleString()}</strong></td>
                      <td className="percentage-cell"><strong>100.00%</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.section>
          ))}

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Pathologist Diagnosis Summary if this is that type of report
  if (isPathologistDiagnosisSummary && hasOverallSummaryStructure) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Pathologist Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Pathologist Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!pathologistDiagnosisData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Pathologist Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(pathologistDiagnosisData, 'Pathologist Diagnosis Summary - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Pathologist Diagnosis Summary</h1>
            <div className="report-date-range">
              {pathologistDiagnosisData ? (
                <>
                  {formatDate(pathologistDiagnosisData.reportDate.startDate)} - {formatDate(pathologistDiagnosisData.reportDate.endDate)}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Overall Summary Section */}
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="section-title">Overall Summary</h2>
            <div className="adequacy-table-container">
              <table className="cytotech-diagnosis-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Objectid</th>
                    <th>Objectid2 (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {pathologistDiagnosisData.overallSummary.map((item, index) => (
                    <tr key={index}>
                      <td>{item.category}</td>
                      <td className="count-cell">{item.objectid.toLocaleString()}</td>
                      <td className="percentage-cell">{item.objectid2.toFixed(1)}%</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td className="count-cell"><strong>{pathologistDiagnosisData.overallTotal.objectid.toLocaleString()}</strong></td>
                    <td className="percentage-cell"><strong>{pathologistDiagnosisData.overallTotal.objectid2.toFixed(1)}%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.section>

          {/* Section Breakdowns */}
          {pathologistDiagnosisData.sections.map((section, sectionIndex) => (
            <motion.section
              key={sectionIndex}
              className="cytotech-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + (sectionIndex * 0.1) }}
            >
              {section.sectionName && <h2 className="section-title">{section.sectionName}</h2>}
              <div className="adequacy-table-container">
                <table className="cytotech-diagnosis-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Objectid</th>
                      <th>Objectid2 (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.categories.map((item, itemIndex) => (
                      <tr key={itemIndex}>
                        <td>{item.category || '-'}</td>
                        <td className="count-cell">{item.objectid.toLocaleString()}</td>
                        <td className="percentage-cell">{item.objectid2.toFixed(1)}%</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td className="count-cell"><strong>{section.total.objectid.toLocaleString()}</strong></td>
                      <td className="percentage-cell"><strong>{section.total.objectid2.toFixed(1)}%</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.section>
          ))}

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Cytotech Diagnosis Summary if this is that type of report
  if (isCytotechDiagnosisSummary && hasOverallSummaryStructure) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Cytotech Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Cytotech Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!cytotechDiagnosisData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Cytotech Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(cytotechDiagnosisData, 'Cytotech Diagnosis Summary - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Cytotech Diagnosis Summary</h1>
            <div className="report-date-range">
              {cytotechDiagnosisData ? (
                <>
                  {formatDate(cytotechDiagnosisData.reportDate.startDate)} - {formatDate(cytotechDiagnosisData.reportDate.endDate)}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Overall Summary Section */}
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="section-title">Overall Summary</h2>
            <div className="adequacy-table-container">
              <table className="cytotech-diagnosis-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>Objectid</th>
                    <th>Objectid2 (%)</th>
                  </tr>
                </thead>
                <tbody>
                  {cytotechDiagnosisData.overallSummary.map((item, index) => (
                    <tr key={index}>
                      <td>{item.category}</td>
                      <td className="count-cell">{item.objectid.toLocaleString()}</td>
                      <td className="percentage-cell">{item.objectid2.toFixed(1)}%</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td className="count-cell"><strong>{cytotechDiagnosisData.overallTotal.objectid.toLocaleString()}</strong></td>
                    <td className="percentage-cell"><strong>{cytotechDiagnosisData.overallTotal.objectid2.toFixed(1)}%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.section>

          {/* Section Breakdowns */}
          {cytotechDiagnosisData.sections.map((section, sectionIndex) => (
            <motion.section
              key={sectionIndex}
              className="cytotech-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + (sectionIndex * 0.1) }}
            >
              <h2 className="section-title">{section.sectionName}</h2>
              <div className="adequacy-table-container">
                <table className="cytotech-diagnosis-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Objectid</th>
                      <th>Objectid2 (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.categories.map((item, itemIndex) => (
                      <tr key={itemIndex}>
                        <td>{item.category || '-'}</td>
                        <td className="count-cell">{item.objectid.toLocaleString()}</td>
                        <td className="percentage-cell">{item.objectid2.toFixed(1)}%</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td className="count-cell"><strong>{section.total.objectid.toLocaleString()}</strong></td>
                      <td className="percentage-cell"><strong>{section.total.objectid2.toFixed(1)}%</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.section>
          ))}

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Cytotech Diagnosis and HPV report
  if (isCytotechDiagnosisAndHpv && hasOverallRowsStructure) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Cytotech Diagnosis and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Cytotech Diagnosis and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!cytotechDiagnosisAndHpvData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Cytotech Diagnosis and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(cytotechDiagnosisAndHpvData, 'Cytotech Diagnosis and HPV - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Cytotech Diagnosis and HPV</h1>
            <div className="report-date-range">
              {cytotechDiagnosisAndHpvData ? (
                <>
                  From {cytotechDiagnosisAndHpvData.reportDate.startDate} To {cytotechDiagnosisAndHpvData.reportDate.endDate}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Main Data Table */}
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="adequacy-table-container">
              <table className="cytotech-diagnosis-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>HPV Result group</th>
                    <th>Objectid</th>
                    <th>Objectid2</th>
                  </tr>
                </thead>
                <tbody>
                  {cytotechDiagnosisAndHpvData.overallRows.map((row, index) => (
                    <tr key={index}>
                      <td>{row.category}</td>
                      <td>{row.hpvResultGroup}</td>
                      <td className="count-cell">{row.objectid.toLocaleString()}</td>
                      <td className="percentage-cell">{row.objectid2.toFixed(0)}%</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan={2}><strong>Total</strong></td>
                    <td className="count-cell"><strong>{cytotechDiagnosisAndHpvData.overallTotal.objectid.toLocaleString()}</strong></td>
                    <td className="percentage-cell"><strong>{cytotechDiagnosisAndHpvData.overallTotal.objectid2.toFixed(0)}%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.section>

          {/* Section Breakdowns by Initials */}
          {cytotechDiagnosisAndHpvData.sections.map((section, sectionIndex) => (
            <motion.section
              key={sectionIndex}
              className="cytotech-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + (sectionIndex * 0.1) }}
            >
              <h2 className="section-title">{section.sectionName}</h2>
              <div className="adequacy-table-container">
                <table className="cytotech-diagnosis-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>HPV Result group</th>
                      <th>Objectid</th>
                      <th>Objectid2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row, itemIndex) => (
                      <tr key={itemIndex}>
                        <td>{row.category || '-'}</td>
                        <td>{row.hpvResultGroup || '-'}</td>
                        <td className="count-cell">{row.objectid.toLocaleString()}</td>
                        <td className="percentage-cell">{row.objectid2.toFixed(0)}%</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td colSpan={2}><strong>Total</strong></td>
                      <td className="count-cell"><strong>{section.total.objectid.toLocaleString()}</strong></td>
                      <td className="percentage-cell"><strong>{section.total.objectid2.toFixed(0)}%</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.section>
          ))}

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Pathologist Diagnosis and HPV report
  if (isPathologistDiagnosisAndHpv && hasOverallRowsStructure) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Pathologist Diagnosis and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Pathologist Diagnosis and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!pathologistDiagnosisAndHpvData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Pathologist Diagnosis and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(pathologistDiagnosisAndHpvData, 'Pathologist Diagnosis and HPV - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Pathologist Diagnosis and HPV</h1>
            <div className="report-date-range">
              {pathologistDiagnosisAndHpvData ? (
                <>
                  From {pathologistDiagnosisAndHpvData.reportDate.startDate} To {pathologistDiagnosisAndHpvData.reportDate.endDate}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Main Data Table */}
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="adequacy-table-container">
              <table className="cytotech-diagnosis-table">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th>HPV Result group</th>
                    <th>Objectid</th>
                    <th>Objectid2</th>
                  </tr>
                </thead>
                <tbody>
                  {pathologistDiagnosisAndHpvData.overallRows.map((row, index) => (
                    <tr key={index}>
                      <td>{row.category}</td>
                      <td>{row.hpvResultGroup}</td>
                      <td className="count-cell">{row.objectid.toLocaleString()}</td>
                      <td className="percentage-cell">{row.objectid2.toFixed(0)}%</td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td colSpan={2}><strong>Total</strong></td>
                    <td className="count-cell"><strong>{pathologistDiagnosisAndHpvData.overallTotal.objectid.toLocaleString()}</strong></td>
                    <td className="percentage-cell"><strong>{pathologistDiagnosisAndHpvData.overallTotal.objectid2.toFixed(0)}%</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.section>

          {/* Section Breakdowns by Initials */}
          {pathologistDiagnosisAndHpvData.sections.map((section, sectionIndex) => (
            <motion.section
              key={sectionIndex}
              className="cytotech-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 + (sectionIndex * 0.1) }}
            >
              <h2 className="section-title">{section.sectionName}</h2>
              <div className="adequacy-table-container">
                <table className="cytotech-diagnosis-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>HPV Result group</th>
                      <th>Objectid</th>
                      <th>Objectid2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row, itemIndex) => (
                      <tr key={itemIndex}>
                        <td>{row.category || '-'}</td>
                        <td>{row.hpvResultGroup || '-'}</td>
                        <td className="count-cell">{row.objectid.toLocaleString()}</td>
                        <td className="percentage-cell">{row.objectid2.toFixed(0)}%</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td colSpan={2}><strong>Total</strong></td>
                      <td className="count-cell"><strong>{section.total.objectid.toLocaleString()}</strong></td>
                      <td className="percentage-cell"><strong>{section.total.objectid2.toFixed(0)}%</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.section>
          ))}

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render HPV Cases with Thinprep report
  if (isHpvCasesWithThinprep && hasHpvTotalThinprepStructure) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'HPV Cases with Thinprep - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'HPV Cases with Thinprep - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!hpvTotalThinprepData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'HPV Cases with Thinprep - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(hpvTotalThinprepData, 'HPV Cases with Thinprep - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">HPV CASES WITH THINPREP</h1>
            <div className="report-date-range">
              {hpvTotalThinprepData ? (
                <>
                  From {hpvTotalThinprepData.reportDate.startDate} To {hpvTotalThinprepData.reportDate.endDate}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Summary Metrics */}
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="hpv-thinprep-summary">
              <div className="summary-metric">
                <div className="metric-label">Total Cases</div>
                <div className="metric-value">{hpvTotalThinprepData.totalCases.toLocaleString()}</div>
              </div>
              <div className="summary-metric">
                <div className="metric-label">Total Specimens</div>
                <div className="metric-value">{hpvTotalThinprepData.totalSpecimens.toLocaleString()}</div>
              </div>
            </div>
          </motion.section>

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Diagnosis and HPV Summary report
  if (isDiagnosisAndHpvSummary && hasDiagnosisAndHpvSummaryStructure) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Diagnosis and HPV Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Diagnosis and HPV Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!diagnosisAndHpvSummaryData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Diagnosis and HPV Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(diagnosisAndHpvSummaryData, 'Diagnosis and HPV Summary - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Diagnosis and HPV Summary</h1>
            <div className="report-date-range">
              {diagnosisAndHpvSummaryData ? (
                <>
                  From {diagnosisAndHpvSummaryData.reportDate.startDate} To {diagnosisAndHpvSummaryData.reportDate.endDate}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Diagnosis Category Sections */}
          {diagnosisAndHpvSummaryData.sections.map((section, sectionIndex) => (
            <motion.section
              key={sectionIndex}
              className="cytotech-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + (sectionIndex * 0.1) }}
            >
              <h2 className="section-title">{section.diagnosisCategory}</h2>
              <div className="adequacy-table-container">
                <table className="cytotech-diagnosis-table">
                  <thead>
                    <tr>
                      <th>HPV Result group</th>
                      <th>Objectid</th>
                      <th>Objectid2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row, itemIndex) => (
                      <tr key={itemIndex}>
                        <td>{row.hpvResultGroup || '-'}</td>
                        <td className="count-cell">{row.objectid.toLocaleString()}</td>
                        <td className="percentage-cell">{row.objectid2.toFixed(0)}%</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td className="count-cell"><strong>{section.total.objectid.toLocaleString()}</strong></td>
                      <td className="percentage-cell"><strong>{section.total.objectid2.toFixed(0)}%</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.section>
          ))}

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Age and HPV report
  if (isAgeAndHpv && hasAgeAndHpvStructure) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Age and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Age and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!ageAndHpvData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Age and HPV - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(ageAndHpvData, 'Age and HPV - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Age and HPV</h1>
            <div className="report-date-range">
              {ageAndHpvData ? (
                <>
                  From {ageAndHpvData.reportDate.startDate} To {ageAndHpvData.reportDate.endDate}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Age Group Sections */}
          {ageAndHpvData.sections.map((section, sectionIndex) => (
            <motion.section
              key={sectionIndex}
              className="cytotech-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + (sectionIndex * 0.1) }}
            >
              <h2 className="section-title">{section.ageGroup}</h2>
              <div className="adequacy-table-container">
                <table className="cytotech-diagnosis-table">
                  <thead>
                    <tr>
                      <th>HPV Result group</th>
                      <th>Objectid</th>
                      <th>Objectid2</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.rows.map((row, itemIndex) => (
                      <tr key={itemIndex}>
                        <td>{row.hpvResultGroup || '-'}</td>
                        <td className="count-cell">{row.objectid.toLocaleString()}</td>
                        <td className="percentage-cell">{row.objectid2.toFixed(0)}%</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td className="count-cell"><strong>{section.total.objectid.toLocaleString()}</strong></td>
                      <td className="percentage-cell"><strong>{section.total.objectid2.toFixed(0)}%</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.section>
          ))}

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Render Gyn Diagnosis Summary if this is that type of report (now uses GynAdequacyBySourceData format)
  if (isGynDiagnosisSummary && hasSourcesStructure) {
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (!gynDiagnosisData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Diagnosis Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(gynDiagnosisData, 'Gyn Diagnosis Summary - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Gyn Diagnosis Summary</h1>
            <div className="report-date-range">
              {gynDiagnosisData ? (
                <>
                  {formatDate(gynDiagnosisData.reportDate.startDate)} - {formatDate(gynDiagnosisData.reportDate.endDate)}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Source Sections */}
          {gynDiagnosisData.sources.map((source, sourceIndex) => (
            <motion.section
              key={sourceIndex}
              className="adequacy-by-source-section"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + (sourceIndex * 0.1) }}
            >
              <h2 className="source-section-title">{source.procSite}</h2>
              <div className="adequacy-table-container">
                <table className="adequacy-by-source-table">
                  <thead>
                    <tr>
                      <th>Diagnosis Category</th>
                      <th>Description</th>
                      <th>Total Cases</th>
                      <th>Percentage</th>
                    </tr>
                  </thead>
                  <tbody>
                    {source.rows.map((row, rowIndex) => {
                      // For diagnosis reports, adequacy field contains the diagnosis category name
                      // If adequacy is null, the description contains the category name
                      const diagnosisCategory = row.adequacy || row.description || '';
                      return (
                        <tr key={rowIndex}>
                          <td>{diagnosisCategory}</td>
                          <td>{row.description || ''}</td>
                          <td className="count-cell">{row.totalCases.toLocaleString()}</td>
                          <td className="percentage-cell">{row.percentage.toFixed(2)}%</td>
                        </tr>
                      );
                    })}
                    <tr className="total-row">
                      <td colSpan={2}><strong>Total</strong></td>
                      <td className="count-cell"><strong>{source.totalCases.toLocaleString()}</strong></td>
                      <td className="percentage-cell"><strong>100.00%</strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </motion.section>
          ))}

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper function to render month/year selector
  const renderMonthYearSelector = () => (
    <motion.section 
      className="month-year-selector-section"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
    >
      <div className="month-year-selector">
        <label htmlFor="month-select" className="selector-label">Month:</label>
        <select
          id="month-select"
          className="month-select"
          value={selectedMonthIndex}
          onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
        >
          {monthNames.map((month, index) => (
            <option key={index} value={index}>
              {month}
            </option>
          ))}
        </select>
        
        <label htmlFor="year-select" className="selector-label">Year:</label>
        <select
          id="year-select"
          className="year-select"
          value={selectedYear}
          onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
        >
          {yearOptions.map((year) => (
            <option key={year} value={year}>
              {year}
            </option>
          ))}
        </select>
      </div>
    </motion.section>
  );

  // Helper function to render report header
  const renderReportHeader = (title: string, reportDate?: { startDate: string; endDate: string }) => (
    <motion.section 
      className="gyn-adequacy-header"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: 0.1 }}
    >
      <h1 className="report-title">{title}</h1>
      <div className="report-date-range">
        {reportDate ? (
          <>
            {formatDate(reportDate.startDate)} - {formatDate(reportDate.endDate)}
          </>
        ) : (
          <>
            {monthNames[selectedMonthIndex]} {selectedYear}
          </>
        )}
      </div>
      <div className="report-issuer">
        <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
        <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
      </div>
    </motion.section>
  );

  // Helper function to render report footer
  const renderReportFooter = () => (
    <div className="report-footer">
      <div className="footer-content">
        <div className="footer-left">
          <p className="footer-text">Laboratory Report Analysis</p>
          <p className="footer-subtext">Detailed metrics and compliance reporting</p>
        </div>
        <div className="footer-right">
          <div className="clinical-squared-branding">
            <p className="powered-by">
              Powered by{' '}
              <a 
                href="https://clinicalsquared.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="clinical-squared-link"
              >
                Clinical Squared
              </a>
              <span className="trademark">®</span>
            </p>
            <p className="company-tagline">Healthcare IT Solutions & Data Analytics</p>
          </div>
        </div>
      </div>
    </div>
  );

  // Render Total Pap Volume
  if (isTotalPapVolume && hasBreakdownStructure) {
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Total Pap Volume - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Total Pap Volume - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (!totalPapVolumeData) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Total Pap Volume - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            {renderMonthYearSelector()}
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header {...getHeaderPropsForReport(totalPapVolumeData, 'Total Pap Volume - Detailed Report')} />
        <div className="report-content">
          {renderMonthYearSelector()}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Total Pap Volume</h1>
            <div className="report-date-range">
              {totalPapVolumeData.reportDate.startDate} to {totalPapVolumeData.reportDate.endDate}
            </div>
          </motion.section>
          
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div style={{ marginBottom: '2rem', fontSize: '1.2rem' }}>
              <p>Total Cases: {totalPapVolumeData.totalCases.toLocaleString()}</p>
            </div>
            <div style={{ fontSize: '1.1rem', lineHeight: '2' }}>
              {totalPapVolumeData.breakdown.map((item, index) => (
                <div key={index} style={{ paddingLeft: '1.5rem', marginBottom: '0.5rem' }}>
                  {item.category}: {item.count.toLocaleString()}
                </div>
              ))}
            </div>
          </motion.section>
          <ReportSignOff reportId={reportId || ''} month={monthYearParam} />
          {renderReportFooter()}
        </div>
      </div>
    );
  }

  // Render Gyn Pap Bx Correlation
  if (isGynPapBxCorrelation) {
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Gyn Pap Bx Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Gyn Pap Bx Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            {renderMonthYearSelector()}
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (!gynPapBxCorrelationData || !hasRowsStructure) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Gyn Pap Bx Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            {renderMonthYearSelector()}
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header {...getHeaderPropsForReport(gynPapBxCorrelationData, 'Gyn Pap Bx Correlation - Detailed Report')} />
        <div className="report-content">
          {renderMonthYearSelector()}
          {renderReportHeader('Gyn Pap Bx Correlation', gynPapBxCorrelationData.reportDate)}
          
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="adequacy-table-container" style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <table className="cytotech-diagnosis-table" style={{ fontSize: '0.8rem', width: '100%', tableLayout: 'fixed', boxSizing: 'border-box' }}>
                <thead>
                  <tr>
                    <th style={{ width: '9%' }}>Patient Name</th>
                    <th style={{ width: '8%' }}>Case Number</th>
                    <th style={{ width: '7%' }}>Case Signout Date</th>
                    <th style={{ width: '17%' }}>Diagnosis Text</th>
                    <th style={{ width: '6%' }}>Diagnosis Code</th>
                    <th style={{ width: '8%' }}>Follow Up Case Number</th>
                    <th style={{ width: '7%' }}>Follow Up Case Signout Date</th>
                    <th style={{ width: '17%' }}>Follow Up Diagnosis Text</th>
                    <th style={{ width: '6%' }}>Follow Up Diagnosis Code</th>
                    <th style={{ width: '6%' }}>Follow Up Discussion</th>
                  </tr>
                </thead>
                <tbody>
                  {gynPapBxCorrelationData.rows && gynPapBxCorrelationData.rows.length > 0 ? (
                    gynPapBxCorrelationData.rows.map((row, index) => (
                      <tr key={index}>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.patientName || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.caseNumber || '-'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.caseSignoutDate)}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisText || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisCode || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.followUpCaseNumber || '-'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.followUpCaseSignoutDate)}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.followUpDiagnosisText || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.followUpDiagnosisCode || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.followUpDiscussion || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} style={{ textAlign: 'center', padding: '2rem' }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.section>
          <ReportSignOff reportId={reportId || ''} month={monthYearParam} />
          {renderReportFooter()}
        </div>
      </div>
    );
  }

  // Render Screening vs Final Diagnosis
  if (isScreeningVsFinalDiagnosis && hasRowsStructure) {
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Screening vs Final Diagnosis - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Screening vs Final Diagnosis - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (!screeningVsFinalDiagnosisData) {
      return (
        <div className="report-view">
          <Header {...getHeaderPropsForReport(screeningVsFinalDiagnosisData, 'Screening vs Final Diagnosis - Detailed Report')} />
          <div className="report-content">
            {renderMonthYearSelector()}
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header title={data ? `${data.title} - Detailed Report` : 'Screening vs Final Diagnosis - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
        <div className="report-content">
          {renderMonthYearSelector()}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Screening vs Final Diagnosis</h1>
            <div className="report-date-range">
              {(() => {
                const formatDateShort = (dateString: string) => {
                  if (!dateString) return '';
                  const date = new Date(dateString);
                  const day = String(date.getDate()).padStart(2, '0');
                  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                  const month = monthNames[date.getMonth()];
                  const year = String(date.getFullYear()).slice(-2);
                  return `${day}-${month}-${year}`;
                };
                return `${formatDateShort(screeningVsFinalDiagnosisData.reportDate.startDate)} to ${formatDateShort(screeningVsFinalDiagnosisData.reportDate.endDate)}`;
              })()}
            </div>
          </motion.section>
          {screeningVsFinalDiagnosisData.patientName && (
            <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 'bold', color: '#ffffff' }}>
              {screeningVsFinalDiagnosisData.patientName}
            </div>
          )}
          
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="adequacy-table-container" style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <table className="cytotech-diagnosis-table" style={{ fontSize: '0.85rem', width: '100%', tableLayout: 'fixed', boxSizing: 'border-box' }}>
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>Case Number</th>
                    <th style={{ width: '12%' }}>Patient Name</th>
                    <th style={{ width: '8%' }}>Source Site</th>
                    <th style={{ width: '10%' }}>Diagnosis Original Code</th>
                    <th style={{ width: '20%' }}>Diagnosis Original Text</th>
                    <th style={{ width: '10%' }}>Diagnosis Code</th>
                    <th style={{ width: '20%' }}>Diagnosis Text</th>
                    <th style={{ width: '10%' }}>Diagnosis Initials</th>
                  </tr>
                </thead>
                <tbody>
                  {screeningVsFinalDiagnosisData.rows && screeningVsFinalDiagnosisData.rows.length > 0 ? (
                    screeningVsFinalDiagnosisData.rows.map((row, index) => (
                      <tr key={index}>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.caseNumber || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.patientName || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.sourceSite || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisOriginalCode || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisOriginalText || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisCode || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisText || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisInitials || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.section>
          {renderReportFooter()}
        </div>
      </div>
    );
  }

  // Render HSIL Retrospective Review
  if (isHSILRetrospectiveReview && hasRowsStructure) {
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'HSIL Retrospective Review - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'HSIL Retrospective Review - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (!hsilRetrospectiveReviewData) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'HSIL Retrospective Review - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            {renderMonthYearSelector()}
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header {...getHeaderPropsForReport(hsilRetrospectiveReviewData, 'HSIL Retrospective Review - Detailed Report')} />
        <div className="report-content">
          {renderMonthYearSelector()}
          {renderReportHeader('HSIL Retrospective Review', hsilRetrospectiveReviewData.reportDate)}
          {hsilRetrospectiveReviewData.patientName && (
            <div style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 'bold', color: '#ffffff' }}>
              {hsilRetrospectiveReviewData.patientName}
            </div>
          )}
          
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="adequacy-table-container" style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <table className="cytotech-diagnosis-table" style={{ fontSize: '0.85rem', width: '100%', tableLayout: 'fixed', boxSizing: 'border-box' }}>
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>Case Number</th>
                    <th style={{ width: '8%' }}>Signout Date</th>
                    <th style={{ width: '20%' }}>Diagnosis Text</th>
                    <th style={{ width: '8%' }}>Diagnosis Code</th>
                    <th style={{ width: '10%' }}>Historical Case Number</th>
                    <th style={{ width: '8%' }}>Historical Signout Date</th>
                    <th style={{ width: '20%' }}>Historical Diagnosis Text</th>
                    <th style={{ width: '8%' }}>Historical Diagnosis Code</th>
                  </tr>
                </thead>
                <tbody>
                  {hsilRetrospectiveReviewData.rows && hsilRetrospectiveReviewData.rows.length > 0 ? (
                    hsilRetrospectiveReviewData.rows.map((row, index) => (
                      <tr key={index}>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.caseNumber || '-'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.signoutDate)}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisText || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisCode || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.historicalCaseNumber || '-'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.historicalSignoutDate)}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.historicalDiagnosisText || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.historicalDiagnosisCode || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} style={{ textAlign: 'center', padding: '2rem' }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.section>
          <ReportSignOff reportId={reportId || ''} month={monthYearParam} />
          {renderReportFooter()}
        </div>
      </div>
    );
  }

  // Render Non Gyn Cytology Diagnosis Correlation
  if (isNonGynCytologyDiagnosisCorrelation && hasRowsStructure) {
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Non Gyn Cytology Diagnosis Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Non Gyn Cytology Diagnosis Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (!nonGynCytologyDiagnosisCorrelationData) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Non Gyn Cytology Diagnosis Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            {renderMonthYearSelector()}
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    const reportDateRange = nonGynCytologyDiagnosisCorrelationData?.reportDate || { startDate: '', endDate: '' };
    
    return (
      <div className="report-view">
        <Header {...getHeaderPropsForReport(nonGynCytologyDiagnosisCorrelationData, 'Non Gyn Cytology Diagnosis Correlation - Detailed Report')} />
        <div className="report-content">
          {renderMonthYearSelector()}
          {renderReportHeader('Non Gyn Cytology Diagnosis Correlation', reportDateRange)}
          
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="adequacy-table-container" style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <table className="cytotech-diagnosis-table" style={{ fontSize: '0.85rem', width: '100%', tableLayout: 'fixed', boxSizing: 'border-box' }}>
                <thead>
                  <tr>
                    <th style={{ width: '10%' }}>Signoutdate</th>
                    <th style={{ width: '12%' }}>Case</th>
                    <th style={{ width: '10%' }}>CytotechDx</th>
                    <th style={{ width: '48%' }}>Diagnosis</th>
                    <th style={{ width: '20%' }}>Pathologist</th>
                  </tr>
                </thead>
                <tbody>
                  {nonGynCytologyDiagnosisCorrelationData.rows && nonGynCytologyDiagnosisCorrelationData.rows.length > 0 ? (
                    nonGynCytologyDiagnosisCorrelationData.rows.map((row, index) => (
                      <tr key={index}>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.signoutDate)}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.case || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.cytotechDx || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosis || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.pathologist || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.section>
          <ReportSignOff reportId={reportId || ''} month={monthYearParam} />
          {renderReportFooter()}
        </div>
      </div>
    );
  }

  // Render Non Gyn Source Correlation
  if (isNonGynSourceCorrelation && hasRowsStructure) {
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Non Gyn Source Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Non Gyn Source Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    if (!nonGynSourceCorrelationData) {
      return (
        <div className="report-view">
          <Header title={data ? `${data.title} - Detailed Report` : 'Non Gyn Source Correlation - Detailed Report'} showBackButton={true} onBack={() => navigate('/dashboard')} />
          <div className="report-content">
            {renderMonthYearSelector()}
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header {...getHeaderPropsForReport(nonGynSourceCorrelationData, 'Non Gyn Source Correlation - Detailed Report')} />
        <div className="report-content">
          {renderMonthYearSelector()}
          {renderReportHeader('Non Gyn Source Correlation', nonGynSourceCorrelationData.reportDate)}
          
          <motion.section 
            className="cytotech-summary-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="adequacy-table-container" style={{ overflowX: 'auto', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
              <table className="cytotech-diagnosis-table" style={{ fontSize: '0.85rem', width: '100%', tableLayout: 'fixed', boxSizing: 'border-box' }}>
                <thead>
                  <tr>
                    <th style={{ width: '12%' }}>Patient Name</th>
                    <th style={{ width: '10%' }}>Case Number</th>
                    <th style={{ width: '8%' }}>Case Signout Date</th>
                    <th style={{ width: '25%' }}>Diagnosis Text</th>
                    <th style={{ width: '10%' }}>Follow Up Case Number</th>
                    <th style={{ width: '8%' }}>Follow Up Case Signout Date</th>
                    <th style={{ width: '27%' }}>Follow Up Diagnosis Text</th>
                  </tr>
                </thead>
                <tbody>
                  {nonGynSourceCorrelationData.rows && nonGynSourceCorrelationData.rows.length > 0 ? (
                    nonGynSourceCorrelationData.rows.map((row, index) => (
                      <tr key={index}>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.patientName || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.caseNumber || '-'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.caseSignoutDate)}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.diagnosisText || '-'}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.followUpCaseNumber || '-'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{formatDate(row.followUpCaseSignoutDate)}</td>
                        <td style={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>{row.followUpDiagnosisText || '-'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '2rem' }}>
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </motion.section>
          {renderReportFooter()}
        </div>
      </div>
    );
  }

  // Render Gyn Adequacy Summary if this is that type of report
  if (isGynAdequacySummary) {
    // Show loading if data is not yet available
    if (reportDataLoading) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading report data...</p>
            </div>
          </div>
        </div>
      );
    }
    
    // Show error if there was an error loading
    if (error) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="error-container">
              <div className="error-message">
                <h3>Error Loading Report</h3>
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    // Show message if no data available
    if (!gynAdequacyData) {
      return (
        <div className="report-view">
          <Header 
            title={data ? `${data.title} - Detailed Report` : 'Gyn Adequacy Summary - Detailed Report'}
            showBackButton={true}
            onBack={() => navigate('/dashboard')}
          />
          <div className="report-content">
            <div className="month-year-selector-section">
              <div className="month-year-selector">
                <label htmlFor="month-select" className="selector-label">Month:</label>
                <select
                  id="month-select"
                  className="month-select"
                  value={selectedMonthIndex}
                  onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
                >
                  {monthNames.map((month, index) => (
                    <option key={index} value={index}>
                      {month}
                    </option>
                  ))}
                </select>
                
                <label htmlFor="year-select" className="selector-label">Year:</label>
                <select
                  id="year-select"
                  className="year-select"
                  value={selectedYear}
                  onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
                >
                  {yearOptions.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="error-container">
              <div className="error-message">
                <h3>No Data Available</h3>
                <p>No data found for {monthNames[selectedMonthIndex]} {selectedYear}.</p>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className="report-view">
        <Header 
          {...getHeaderPropsForReport(gynAdequacyData, 'Gyn Adequacy Summary - Detailed Report')}
        />

        <div className="report-content">
          {/* Month/Year Selector */}
          <motion.section 
            className="month-year-selector-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="month-year-selector">
              <label htmlFor="month-select" className="selector-label">Month:</label>
              <select
                id="month-select"
                className="month-select"
                value={selectedMonthIndex}
                onChange={(e) => handleMonthYearChange(parseInt(e.target.value), selectedYear)}
              >
                {monthNames.map((month, index) => (
                  <option key={index} value={index}>
                    {month}
                  </option>
                ))}
              </select>
              
              <label htmlFor="year-select" className="selector-label">Year:</label>
              <select
                id="year-select"
                className="year-select"
                value={selectedYear}
                onChange={(e) => handleMonthYearChange(selectedMonthIndex, parseInt(e.target.value))}
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </motion.section>

          {/* Report Header */}
          <motion.section 
            className="gyn-adequacy-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h1 className="report-title">Gyn Adequacy Summary</h1>
            <div className="report-date-range">
              {gynAdequacyData ? (
                <>
                  {formatDate(gynAdequacyData.reportDate.startDate)} - {formatDate(gynAdequacyData.reportDate.endDate)}
                </>
              ) : (
                <>
                  {monthNames[selectedMonthIndex]} {selectedYear}
                </>
              )}
            </div>
            <div className="report-issuer">
              <p className="issuer-name">CoCoPath - Contra Costa Pathology Associates</p>
              <p className="issuer-address">399 Taylor Blvd, Suite 200, Pleasant Hill, CA 94523</p>
            </div>
          </motion.section>

          {/* Satisfactory Section */}
          <motion.section 
            className="adequacy-section satisfactory-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h2 className="section-title">Satisfactory</h2>
            <div className="adequacy-table-container">
              <table className="adequacy-table">
                <thead>
                  <tr>
                    <th>Comment</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {gynAdequacyData?.satisfactory?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.comment}</td>
                      <td className="count-cell">{item.count.toLocaleString()}</td>
                    </tr>
                  )) || []}
                  <tr className="total-row">
                    <td><strong>Total Satisfactory</strong></td>
                    <td className="count-cell"><strong>{gynAdequacyData?.totalSatisfactory?.toLocaleString() || '0'}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.section>

          {/* Unsatisfactory Section */}
          <motion.section 
            className="adequacy-section unsatisfactory-section"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h2 className="section-title">Unsatisfactory</h2>
            <div className="adequacy-table-container">
              <table className="adequacy-table">
                <thead>
                  <tr>
                    <th>Comment</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {gynAdequacyData?.unsatisfactory?.map((item, index) => (
                    <tr key={index}>
                      <td>{item.comment}</td>
                      <td className="count-cell">{item.count.toLocaleString()}</td>
                    </tr>
                  )) || []}
                  <tr className="total-row">
                    <td><strong>Total Unsatisfactory</strong></td>
                    <td className="count-cell"><strong>{gynAdequacyData?.totalUnsatisfactory?.toLocaleString() || '0'}</strong></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </motion.section>

          {/* Summary Stats */}
          <motion.section 
            className="adequacy-summary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="summary-cards">
              <div className="summary-card">
                <div className="summary-label">Total Cases</div>
                <div className="summary-value">{gynAdequacyData.totalCases.toLocaleString()}</div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Satisfactory Rate</div>
                <div className="summary-value">
                  {gynAdequacyData.totalCases > 0
                    ? ((gynAdequacyData.totalSatisfactory / gynAdequacyData.totalCases) * 100).toFixed(1)
                    : '0.0'}%
                </div>
              </div>
              <div className="summary-card">
                <div className="summary-label">Unsatisfactory Rate</div>
                <div className="summary-value">
                  {gynAdequacyData.totalCases > 0
                    ? ((gynAdequacyData.totalUnsatisfactory / gynAdequacyData.totalCases) * 100).toFixed(1)
                    : '0.0'}%
                </div>
              </div>
            </div>
          </motion.section>

          {/* Clinical Squared Branding */}
          <div className="report-footer">
            <div className="footer-content">
              <div className="footer-left">
                <p className="footer-text">
                  Laboratory Report Analysis
                </p>
                <p className="footer-subtext">
                  Detailed metrics and compliance reporting
                </p>
              </div>
              <div className="footer-right">
                <div className="clinical-squared-branding">
                  <p className="powered-by">
                    Powered by{' '}
                    <a 
                      href="https://clinicalsquared.com" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="clinical-squared-link"
                    >
                      Clinical Squared
                    </a>
                    <span className="trademark">®</span>
                  </p>
                  <p className="company-tagline">
                    Healthcare IT Solutions & Data Analytics
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // If we get here and don't have data, and it's not gyn-adequacy-summary, something went wrong
  if (!data && !isGynAdequacySummary) {
    return (
      <div className="report-view">
        <Header 
          title="Report Not Found"
          showBackButton={true}
          onBack={() => navigate('/dashboard')}
        />
        <div className="error-container">
          <div className="error-message">
            <h3>Report Not Found</h3>
            <p>The requested report could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  // At this point, data should exist for non-gyn-adequacy-summary reports
  // Handle both LaboratoryReportData (has 'procedure') and TotalPapVolumeData (has 'category')
  const departmentData = (reportData && 'breakdown' in reportData ? reportData.breakdown : [
    { procedure: 'Pap Smear', value: 35, percentage: 35, cliaCompliant: true },
    { procedure: 'Non-Gyn Cytology', value: 25, percentage: 25, cliaCompliant: true },
    { procedure: 'Histopathology', value: 20, percentage: 20, cliaCompliant: true },
    { procedure: 'Laboratory Tests', value: 15, percentage: 15, cliaCompliant: true },
    { procedure: 'Radiology', value: 5, percentage: 5, cliaCompliant: true }
  ]).map((item, index) => ({
    ...item,
    name: 'procedure' in item ? item.procedure : ('category' in item ? item.category : 'Unknown'),
    color: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7'][index]
  }));

  // TypeScript guard: data must exist here for regular reports
  if (!data) {
    return null;
  }

  return (
    <div className="report-view">
      <Header 
        title={`${data.title} - Detailed Report`}
        showBackButton={true}
        onBack={() => navigate('/dashboard')}
      />

      <div className="report-content">
        {/* Overview Section */}
        <motion.section 
          className="overview-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="overview-cards">
            <div className="overview-card primary">
              <h3>Current Value</h3>
              <div className="value-large">{formatValue(data.currentValue)}</div>
              <div className="unit">{data.unit}</div>
            </div>
            <div className="overview-card">
              <h3>Previous Month</h3>
              <div className="value">{formatValue(data.previousValue)}</div>
              <div className="unit">{data.unit}</div>
            </div>
            <div className="overview-card">
              <h3>Trend</h3>
              <div className={`trend-value ${data.trend}`}>
                {data.trendPercentage > 0 ? '+' : ''}{data.trendPercentage.toFixed(1)}%
              </div>
              <div className="trend-label">Month-over-Month</div>
            </div>
            <div className="overview-card">
              <h3>Priority</h3>
              <div className={`priority-badge ${data.priority}`}>
                {data.priority.toUpperCase()}
              </div>
            </div>
          </div>
        </motion.section>

        {/* Month Selector */}
        <motion.section 
          className="month-selector-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
        >
          <h2>Select Month for Detailed Analysis</h2>
          <div className="month-buttons">
            {data.monthlyData.map((monthData) => (
              <motion.button
                key={monthData.month}
                className={`month-button ${selectedMonth === monthData.month ? 'active' : ''}`}
                onClick={() => setSelectedMonth(monthData.month)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="month-name">{monthData.month}</div>
                <div className="month-value">{formatValue(monthData.value)}</div>
              </motion.button>
            ))}
          </div>
        </motion.section>

        {/* Charts Section */}
        <div className="charts-section">
          {/* Trend Chart */}
          <motion.div 
            className="chart-container"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3>12-Month Trend Analysis</h3>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={data.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip 
                  formatter={(value: number) => [formatValue(value), data.title]}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Area 
                  type="monotone" 
                  dataKey="value" 
                  stroke={data.color} 
                  fill={data.color}
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Department Breakdown */}
          <motion.div 
            className="chart-container"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <h3>Department Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={departmentData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(props: any) => `${props.name} ${(props.percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {departmentData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Performance vs Target */}
          <motion.div 
            className="chart-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <h3>Performance vs Target</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.monthlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill={data.color} />
                {data.monthlyData[0].target && (
                  <Bar dataKey="target" fill="#8884d8" opacity={0.7} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </motion.div>

          {/* Key Metrics */}
          <motion.div 
            className="metrics-container"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <h3>Key Performance Indicators</h3>
            <div className="metrics-grid">
              <div className="metric-item">
                <div className="metric-label">Efficiency Score</div>
                <div className="metric-value">87.3%</div>
                <div className="metric-trend up">+2.1%</div>
              </div>
              <div className="metric-item">
                <div className="metric-label">Quality Rating</div>
                <div className="metric-value">4.8/5.0</div>
                <div className="metric-trend up">+0.2</div>
              </div>
              <div className="metric-item">
                <div className="metric-label">Patient Satisfaction</div>
                <div className="metric-value">92.1%</div>
                <div className="metric-trend stable">0.0%</div>
              </div>
              <div className="metric-item">
                <div className="metric-label">Cost per Patient</div>
                <div className="metric-value">$1,247</div>
                <div className="metric-trend down">-3.2%</div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Insights Section */}
        <motion.section 
          className="insights-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h2>Key Insights & Recommendations</h2>
          <div className="insights-grid">
            <div className="insight-card">
              <h4>📈 Growth Opportunity</h4>
              <p>Based on current trends, implementing targeted interventions could increase efficiency by 15-20% over the next quarter.</p>
            </div>
            <div className="insight-card">
              <h4>⚠️ Risk Assessment</h4>
              <p>Monitor the declining trend in Q3 metrics. Early intervention recommended to prevent further deterioration.</p>
            </div>
            <div className="insight-card">
              <h4>💡 Optimization</h4>
              <p>Resource allocation adjustments in Emergency and Surgery departments could improve overall performance.</p>
            </div>
            <div className="insight-card">
              <h4>🎯 Strategic Focus</h4>
              <p>Concentrate efforts on high-impact areas identified through trend analysis to maximize ROI.</p>
            </div>
          </div>
        </motion.section>

        {/* Clinical Squared Branding */}
        <div className="report-footer">
          <div className="footer-content">
            <div className="footer-left">
              <p className="footer-text">
                Laboratory Report Analysis
              </p>
              <p className="footer-subtext">
                Detailed metrics and compliance reporting
              </p>
            </div>
            <div className="footer-right">
              <div className="clinical-squared-branding">
                <p className="powered-by">
                  Powered by{' '}
                  <a 
                    href="https://clinicalsquared.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="clinical-squared-link"
                  >
                    Clinical Squared
                  </a>
                  <span className="trademark">®</span>
                </p>
                <p className="company-tagline">
                  Healthcare IT Solutions & Data Analytics
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportView;
